/**
 * Le categorie dei tipi di scadenza.
 *
 * Fino al punto 3b erano tre valori scritti nel codice: aggiungerne una
 * richiedeva una migrazione e un rilascio. Ora sono un'anagrafica come
 * soggetti e tipi — stessa forma di schermata, apposta: chi ha imparato a
 * usarne una le sa usare tutte.
 *
 * **Modificabili da amministratori e redattori**, come il catalogo dei tipi: una
 * categoria in meno cambia come l'intero ufficio classifica le sue scadenze.
 * Gli operatori la vedono, e basta.
 */
import { useCallback, useEffect, useState } from 'react';

import { ErroreApi } from '../api/client.ts';
import { categorie as apiCategorie } from '../api/scadenziario.ts';
import type { Categoria } from '../api/tipi-dati.ts';
import { Dialogo } from '../componenti/Dialogo.tsx';
import {
  DialogoEliminazione,
  ZonaPericolosa,
  type BersaglioEliminazione,
} from '../componenti/DialogoEliminazione.tsx';
import { useAutenticazione } from '../auth/contesto.tsx';
import { IntestazioneOrdinabile, useOrdinamento } from '../componenti/IntestazioneOrdinabile.tsx';
import { ordinaRighe, type Valore } from '../componenti/ordinamento.ts';
import { puoRedigere } from '../auth/ruoli.ts';

/** La descrizione è facoltativa: quando manca la riga finisce in fondo. */
function valoreCategoria(c: Categoria, chiave: string): Valore {
  switch (chiave) {
    case 'nome': return c.nome;
    case 'descrizione': return c.descrizione ?? null;
    case 'tipi': return c.tipi ?? 0;
    default: return null;
  }
}

export function Categorie() {
  const { ordinamento, ordina } = useOrdinamento();
  const { utente } = useAutenticazione();
  const puoModificare = puoRedigere(utente?.ruolo);

  const [elementi, setElementi] = useState<Categoria[]>([]);
  const [mostraDisattivate, setMostraDisattivate] = useState(false);
  const [inCaricamento, setInCaricamento] = useState(true);
  const [errore, setErrore] = useState<string | null>(null);
  const [conferma, setConferma] = useState<string | null>(null);
  /** `undefined` = chiuso · `null` = nuova · oggetto = modifica di quella. */
  const [inForm, setInForm] = useState<Categoria | null | undefined>(undefined);
  const [daEliminare, setDaEliminare] = useState<BersaglioEliminazione | null>(null);
  const [ricarica, setRicarica] = useState(0);

  useEffect(() => {
    let annullato = false;
    setInCaricamento(true);

    apiCategorie
      .elenca(mostraDisattivate ? { limite: 200 } : { attiva: 'true', limite: 200 })
      .then((r) => { if (!annullato) { setElementi(r.elementi); setErrore(null); } })
      .catch((e: unknown) => {
        if (!annullato) setErrore(e instanceof Error ? e.message : 'Errore imprevisto');
      })
      .finally(() => { if (!annullato) setInCaricamento(false); });

    return () => { annullato = true; };
  }, [mostraDisattivate, ricarica]);

  const dopoAzione = useCallback((messaggio: string) => {
    setInForm(undefined);
    setDaEliminare(null);
    setConferma(messaggio);
    setRicarica((n) => n + 1);
  }, []);

  async function commutaAttiva(c: Categoria) {
    try {
      if (c.attiva) {
        await apiCategorie.disattiva(c.id);
        dopoAzione(`«${c.nome}» disattivata: non comparirà più fra le scelte.`);
      } else {
        await apiCategorie.riattiva(c.id);
        dopoAzione(`«${c.nome}» riattivata.`);
      }
    } catch (e) {
      setErrore(e instanceof ErroreApi ? e.message : 'Errore imprevisto');
    }
  }

  return (
    <>
      {/* Stessa impalcatura di Soggetti e Tipi: conteggio fuori dall'`h1` e
          pulsante con `azione-titolo`, che è ciò che lo manda a destra. */}
      <div className="titolo-pagina">
        <h1>Categorie</h1>
        <span className="conteggio">{elementi.length} nel catalogo</span>
        {puoModificare && (
          <button
            type="button"
            className="bottone bottone-principale azione-titolo"
            onClick={() => setInForm(null)}
          >
            <span aria-hidden="true">+</span> Nuova categoria
          </button>
        )}
      </div>

      <p className="sottotitolo-pagina">
        Una categoria è il raggruppamento dei tipi di scadenza, ed è la ripartizione che si
        legge nel cruscotto. Cambiarne il nome non tocca niente: i tipi la seguono, perché la
        conoscono per riferimento e non per nome.
        {!puoModificare && ' Solo un amministratore o un redattore può modificarle.'}
      </p>

      {conferma !== null && (
        <div className="avviso-esito" role="status">
          {conferma}
          <button type="button" className="bottone-piatto" onClick={() => setConferma(null)}>Chiudi</button>
        </div>
      )}
      {errore !== null && <div className="avviso-errore" role="alert">{errore}</div>}

      <div className="filtri">
        <label className="interruttore">
          <input
            type="checkbox"
            checked={mostraDisattivate}
            onChange={(e) => setMostraDisattivate(e.target.checked)}
          />
          Mostra anche le disattivate
        </label>
      </div>

      <div className="pannello">
        {inCaricamento ? (
          <p className="stato-caricamento">Caricamento…</p>
        ) : elementi.length === 0 ? (
          <p className="stato-vuoto">Nessuna categoria nel catalogo.</p>
        ) : (
          <div className="tabella-contenitore">
            <table className="tabella-centrata">
              <caption className="solo-lettori-schermo">Categorie dei tipi di scadenza</caption>
              <thead>
                <tr>
                  <IntestazioneOrdinabile chiave="nome" ordinamento={ordinamento} onOrdina={ordina}>Nome</IntestazioneOrdinabile>
                  <IntestazioneOrdinabile chiave="descrizione" ordinamento={ordinamento} onOrdina={ordina}>Descrizione</IntestazioneOrdinabile>
                  <IntestazioneOrdinabile chiave="tipi" ordinamento={ordinamento} onOrdina={ordina}>Tipi</IntestazioneOrdinabile>
                  {puoModificare && <th scope="col" className="col-azione">Azione</th>}
                </tr>
              </thead>
              <tbody>
                {ordinaRighe(elementi, ordinamento, valoreCategoria).map((c) => (
                  <tr key={c.id} className={c.attiva ? '' : 'riga-annullata'}>
                    <td>
                      <span className="soggetto-nome">{c.nome}</span>
                      {!c.attiva && <span className="badge badge-annullata" style={{ marginLeft: 8 }}>
                        <span aria-hidden="true">⊘</span> Disattivata
                      </span>}
                    </td>
                    <td className="tipo-nome">
                      {c.descrizione ?? <span className="tenue">—</span>}
                    </td>
                    <td className="numero">{c.tipi ?? 0}</td>
                    {/* Solo «Modifica» e «Disattiva», come in soggetti e tipi:
                        l'eliminazione sta dentro la scheda, in fondo. Un
                        pulsante rosso in ogni riga di un elenco è a un clic
                        distratto di distanza dal danno, e qui il danno è una
                        categoria che si porta dietro la classificazione di
                        tutto l'ufficio. */}
                    {puoModificare && (
                      <td className="col-azione">
                        <div className="cella-azioni">
                          <button type="button" className="bottone" onClick={() => setInForm(c)}>
                            <span aria-hidden="true">✎</span> Modifica
                          </button>
                          <button
                            type="button"
                            className="bottone"
                            onClick={() => void commutaAttiva(c)}
                          >
                            {c.attiva ? 'Disattiva' : 'Riattiva'}
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {inForm !== undefined && (
        <FormCategoria
          categoria={inForm}
          // Il catalogo lo tocca solo la redazione, quindi qui il permesso
          // di modificare e quello di eliminare coincidono.
          puoEliminare={puoModificare}
          onChiudi={() => setInForm(undefined)}
          onSalvata={dopoAzione}
          // Si chiude la scheda prima di aprire la conferma: due dialoghi
          // modali sovrapposti si contenderebbero il fuoco della tastiera, e
          // il tasto Esc chiuderebbe quello sbagliato.
          onElimina={(c) => {
            setInForm(undefined);
            setDaEliminare({
              entita: 'categoria',
              id: c.id,
              nome: c.nome,
              etichettaNome: 'il nome della categoria',
            });
          }}
        />
      )}

      {daEliminare !== null && (
        <DialogoEliminazione
          bersaglio={daEliminare}
          onChiudi={() => setDaEliminare(null)}
          onEliminato={dopoAzione}
        />
      )}
    </>
  );
}

function FormCategoria({
  categoria,
  puoEliminare,
  onChiudi,
  onSalvata,
  onElimina,
}: {
  categoria: Categoria | null;
  puoEliminare: boolean;
  onChiudi: () => void;
  onSalvata: (messaggio: string) => void;
  onElimina: (categoria: Categoria) => void;
}) {
  const [nome, setNome] = useState(categoria?.nome ?? '');
  const [descrizione, setDescrizione] = useState(categoria?.descrizione ?? '');
  const [errore, setErrore] = useState<string | null>(null);
  const [inCorso, setInCorso] = useState(false);

  async function salva() {
    setErrore(null);
    setInCorso(true);

    const dati = {
      nome: nome.trim(),
      descrizione: descrizione.trim() === '' ? null : descrizione.trim(),
    };

    try {
      if (categoria === null) {
        const creata = await apiCategorie.crea(dati);
        onSalvata(`Creata la categoria «${creata.nome}».`);
      } else {
        await apiCategorie.modifica(categoria.id, dati);
        onSalvata(`«${dati.nome}» aggiornata.`);
      }
    } catch (e) {
      setErrore(e instanceof ErroreApi ? e.message : 'Errore imprevisto');
      setInCorso(false);
    }
  }

  return (
    <Dialogo
      titolo={categoria === null ? 'Nuova categoria' : 'Modifica categoria'}
      aperto
      onChiudi={onChiudi}
    >
      {errore !== null && <div className="avviso-errore" role="alert">{errore}</div>}

      <form onSubmit={(e) => { e.preventDefault(); void salva(); }}>
        <div className="campo">
          <label htmlFor="c-nome">
            Nome <span className="obbligatorio">obbligatorio</span>
          </label>
          <input
            id="c-nome"
            type="text"
            value={nome}
            onChange={(e) => setNome(e.target.value)}
            placeholder="es. Certificazioni e formazione"
            required
            autoFocus
          />
          <p className="aiuto">
            È il titolo che si legge nel cruscotto e nei filtri. Dev'essere unico.
          </p>
        </div>

        <div className="campo">
          <label htmlFor="c-descr">
            Descrizione <span className="tenue">facoltativa</span>
          </label>
          <input
            id="c-descr"
            type="text"
            value={descrizione}
            onChange={(e) => setDescrizione(e.target.value)}
            placeholder="es. Dipendenti · attestati, corsi, visite mediche"
          />
          <p className="aiuto">
            La riga sotto al nome nel cruscotto. Serve a distinguere due categorie che dal solo
            titolo si somigliano: se non aggiunge niente, si lascia vuota.
          </p>
        </div>

        <div className="dialogo-azioni">
          <button type="submit" className="bottone bottone-principale" disabled={inCorso}>
            {inCorso ? 'Salvataggio…' : categoria === null ? 'Crea categoria' : 'Salva modifiche'}
          </button>
          <button type="button" className="bottone-piatto" onClick={onChiudi} disabled={inCorso}>
            Annulla
          </button>
        </div>
      </form>

      {/* Fuori dal form e in fondo a tutto, come in soggetti e tipi: ci si
          arriva solo aprendo la scheda di una categoria precisa e scorrendo
          oltre «Salva modifiche». Su una categoria nuova non c'è niente da
          cancellare. */}
      {categoria !== null && puoEliminare && (
        <ZonaPericolosa
          spiegazione={
            'Toglie la categoria dal catalogo per sempre. I tipi che la usavano restano al ' +
            'loro posto ma senza categoria, da riassegnare, e le loro scadenze si ritrovano ' +
            "nell'elenco sotto il filtro «Senza categoria». " +
            "Per smettere di proporla nei menu lasciando i tipi dove sono c'è «Disattiva»."
          }
          onElimina={() => onElimina(categoria)}
        />
      )}
    </Dialogo>
  );
}
